import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Clock, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const WARNING_AFTER_MS = 25 * 60 * 1000; // warn 5 minutes before logout
const LOGOUT_AFTER_MS = 30 * 60 * 1000;  // 30 minutes of inactivity

export const SessionTimeout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const expiredRef = useRef(false);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(300);

  const handleExpire = useCallback(async () => {
    if (expiredRef.current) return;
    expiredRef.current = true;
    try {
      await api.post('/auth/session-expired');
    } catch (e) {
      // token may already be invalid; still log out locally
    }
    setShowWarning(false);
    warningShownRef.current = false;
    logout({ silent: true });
    toast.warning('You were logged out after 30 minutes of inactivity');
    navigate('/admin', { replace: true });
    expiredRef.current = false;
  }, [logout, navigate]);

  const continueSession = () => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    setShowWarning(false);
  };

  // Navigation counts as activity
  useEffect(() => {
    if (!warningShownRef.current) {
      lastActivityRef.current = Date.now();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    lastActivityRef.current = Date.now();

    const onActivity = () => {
      if (!warningShownRef.current) {
        lastActivityRef.current = Date.now();
      }
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, onActivity, true));

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= LOGOUT_AFTER_MS) {
        handleExpire();
      } else if (idle >= WARNING_AFTER_MS) {
        warningShownRef.current = true;
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((LOGOUT_AFTER_MS - idle) / 1000)));
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity, true));
      clearInterval(interval);
    };
  }, [user, handleExpire]);

  if (!user) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent className="max-w-sm [&>button]:hidden" data-testid="session-timeout-warning">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Session Expiring Soon
          </DialogTitle>
        </DialogHeader>
        <div className="text-center py-2">
          <p className="text-sm text-slate-600">
            You have been inactive. For security, you will be automatically logged out in:
          </p>
          <p className="text-4xl font-bold text-amber-600 my-3 font-mono" data-testid="session-countdown">
            {minutes}:{String(seconds).padStart(2, '0')}
          </p>
          <p className="text-xs text-slate-400">Click "Continue Session" to stay logged in.</p>
        </div>
        <DialogFooter className="gap-2 sm:justify-center">
          <Button
            variant="outline"
            onClick={handleExpire}
            className="text-red-600 border-red-200"
            data-testid="session-logout-now-btn"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Log Out Now
          </Button>
          <Button onClick={continueSession} className="btn-primary" data-testid="session-continue-btn">
            Continue Session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeout;
